import { faBarsFilter } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Drawer from "components/drawerSlide/drawer";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./depositFiltersDrawer.scss";
import FormLayout from "components/form/FormLayout";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Input from "components/form/input";
import { useAppSelector } from "hooks/redux";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import Checkbox from "components/form/checkbox/Checkbox";
import { Badge } from "components/badge/Badge";

type TDepositStatus = "applied" | "partially_refunded" | "refunded" | "pending";

interface IProps {
    open: boolean;
    filters: IDepositFilters;
    onClose: () => void;
    onOk: (updatedSelection: IDepositFilters) => void;
}

export interface IDepositFilters {
    offset: number;
    search: string;
    booking_date: Date;
    deposit_date: Date;
    min_amount: string;
    max_amount: string;
    status: Array<TDepositStatus>;
}

export default function DepositFiltersDrawer(props: IProps) {
    const { open, filters, onClose, onOk } = props;
    const { t } = useTranslation();
    const facilityStore = useAppSelector(store => store.facilityStore);
    const { getCurrencySymbol } = useCurrencyFacility();
    const [state, setState] = useState<IDepositFilters>({
        offset: 0,
        search: "",
        booking_date: null,
        deposit_date: null,
        min_amount: "",
        max_amount: "",
        status: null,
    });

    useEffect(() => {
        if (open && filters) {
            setState({ ...filters });
        }
    }, [open, filters]);

    function updateStatusSelection(status: TDepositStatus) {
        let updatedSelection = state?.status ? [...state.status] : [];
        if (updatedSelection?.includes(status)) {
            updatedSelection = updatedSelection?.filter(s => s !== status);
        } else {
            updatedSelection.push(status);
        }
        setState(prev => ({ ...prev, status: updatedSelection }));
    }

    function clearSelection() {
        setState(prev => ({
            ...prev,
            // offset: 0,
            // search: "",
            booking_date: null,
            deposit_date: null,
            min_amount: "",
            max_amount: "",
            status: null,
        }));
    }

    function handleOk() {
        void onOk({ ...state });
        void onClose();
    }

    return (
        <Drawer
            open={open}
            headerLeading={
                <span className="deposit-filters-drawer-icon" aria-hidden="true">
                    <FontAwesomeIcon icon={faBarsFilter} />
                </span>}
            title={t("Filter booking deposits by")}
            subtitle={t("Apply filters to table data")}
            onClose={onClose}
            cancelText={t("Cancel")}
            cancelButtonType="text"
            showCancel
            backDropClose
            secondaryActions={[{ label: t("Clear all"), action: () => clearSelection(), buttonType: "secondary" }]}
            onOk={handleOk}
            okText={t("Apply")}
        >
            <Drawer.Section bordered>
                <FormLayout>
                    <FormLayout.Group>
                        <DatePickerInput
                            months={1}
                            position="left"
                            startingDate={state?.booking_date}
                            setStartingDate={(date: Date) => setState(prev => ({ ...prev, booking_date: date }))}
                            label={t("Booking date")}
                            allowEmptySingleDate
                            allowClear
                        />
                    </FormLayout.Group>
                </FormLayout>
            </Drawer.Section>
            <Drawer.Section bordered>
                <FormLayout>
                    <FormLayout.Group>
                        <DatePickerInput
                            months={1}
                            position="left"
                            startingDate={state?.deposit_date}
                            setStartingDate={(date: Date) => setState(prev => ({ ...prev, deposit_date: date }))}
                            label={t("Deposit date")}
                            allowEmptySingleDate
                            allowClear
                        />
                    </FormLayout.Group>
                </FormLayout>
            </Drawer.Section>
            <Drawer.Section bordered>
                <p className="deposit-filters-drawer-heading">{t("Amount")}</p>
                <FormLayout>
                    <FormLayout.Group>
                        <Input
                            label={t("Min.")}
                            value={state?.min_amount}
                            onChange={e => setState(prev => ({ ...prev, min_amount: e.target.value }))}
                            type="number"
                            prefix={getCurrencySymbol(facilityStore?.facility?.default_currency_code) ?? "$"}
                            placeholder={"10"} />
                        <Input
                            label={t("Max.")}
                            value={state?.max_amount}
                            onChange={e => setState(prev => ({ ...prev, max_amount: e.target.value }))}
                            type="number"
                            prefix={getCurrencySymbol(facilityStore?.facility?.default_currency_code) ?? "$"}
                            placeholder={"25"} />
                    </FormLayout.Group>
                </FormLayout>
            </Drawer.Section>
            <Drawer.Section bordered>
                <p className="deposit-filters-drawer-heading">{t("Status")}</p>
                <FormLayout>
                    <FormLayout.Group>
                        <Checkbox
                            checked={state?.status?.includes("applied")}
                            labelHidden
                            size="small"
                            onChange={() => updateStatusSelection("applied")}
                            label={<Badge type="success" outline size="small">{t("Applied")}</Badge>} />
                    </FormLayout.Group>
                    <FormLayout.Group>
                        <Checkbox
                            checked={state?.status?.includes("partially_refunded")}
                            labelHidden
                            size="small"
                            onChange={() => updateStatusSelection("partially_refunded")}
                            label={<Badge colour="#B93815" backgroundColour="#FFF4ED" type="warning" outline size="small">{t("Partial refund")}</Badge>} />
                    </FormLayout.Group>
                    <FormLayout.Group>
                        <Checkbox
                            checked={state?.status?.includes("refunded")}
                            labelHidden size="small"
                            onChange={() => updateStatusSelection("refunded")}
                            label={<Badge type="warning" outline size="small">{t("Refunded")}</Badge>} />
                    </FormLayout.Group>
                    <FormLayout.Group>
                        <Checkbox
                            checked={state?.status?.includes("pending")}
                            labelHidden
                            size="small" onChange={() => updateStatusSelection("pending")}
                            label={<Badge type="gray" outline size="small">{t("Pending")}</Badge>} />
                    </FormLayout.Group>
                </FormLayout>
            </Drawer.Section>
        </Drawer>
    );
}